'use client'

import React, { useEffect } from 'react'
import { reportWebException } from '@/lib/error-reporting'
import { DEFAULT_WEB_LOCALE, UI_LOCALE_COOKIE, type WebLocale } from '@/lib/locale'

/**
 * The boundary of last resort: the one error screen that can render when the
 * ROOT LAYOUT itself threw.
 *
 * Next replaces the root layout with this file when it is active, which is why
 * it ships its own `<html>` and `<body>` — and why it is written to depend on as
 * little of the app as possible. Everything the root layout mounts is, by
 * definition, a suspect when this renders: the i18n provider, the auth-modal
 * provider, the lightbox provider, the font variables, the Sentry loader tag.
 * Re-mounting any of them here would re-run the code that may have just thrown,
 * and a `global-error` that throws gets replaced by Next's built-in 500 page —
 * i.e. the app would lose its own error screen precisely when it needs it.
 *
 * That constraint drives the three things that look unusual below.
 *
 * 1. **The copy does not go through `t()`.** `t()` needs `I18nProvider`, which
 *    is mounted in the root layout and is therefore one of the candidates for
 *    what broke. The two strings are inlined instead, in both locales, and the
 *    locale is resolved from the SAME cookie the server reads — the constants
 *    come from `lib/locale.ts`, which is deliberately pure (no React, no
 *    `next/*`), so importing it here costs nothing and cannot fail. This is the
 *    one place in `apps/web` where a user-visible string is not a `t()` call;
 *    it is a deliberate exception to the repo's i18n rule, not an oversight, and
 *    it is bilingual rather than English-only.
 * 2. **The styles are inline, not `globals.css`.** Next's own docs are explicit
 *    that `global-error` renders its own document and does not pick up global
 *    styles; importing the stylesheet would also make the stylesheet pipeline a
 *    dependency of the screen that exists for when the pipeline is broken.
 *    There is little to style — this is one sentence and one button.
 * 3. **The report goes out through the Loader stub, not a fresh SDK.**
 *    `reportWebException` no-ops when `window.Sentry` is absent (see its
 *    docblock), so this cannot turn one failure into two. If the root layout
 *    threw during SSR the loader tag never rendered, and this report is simply
 *    dropped — the server-side log and `error.digest` are the record in that
 *    case. That is the honest limit of client-side reporting here.
 *
 * `retry()` (not `reset()`): the root layout reads `cookies()` / `headers()` and
 * fetches, so the useful recovery is re-fetching and re-rendering, not merely
 * clearing the boundary's state.
 */

const COPY: Record<WebLocale, { title: string; description: string; retry: string }> = {
  'zh-TW': {
    title: '這個頁面出了點問題',
    description: '我們已經收到錯誤回報。重新載入通常就會恢復；若持續發生，請聯絡 support@kiao.com.tw。',
    retry: '重新載入',
  },
  en: {
    title: 'Something went wrong on this page',
    description:
      'The error has been reported. Reloading usually fixes it; if it keeps happening, contact support@kiao.com.tw.',
    retry: 'Reload',
  },
}

/**
 * The UI locale, read from the same cookie `resolveLocale()` reads on the
 * server. `Accept-Language` is not available to a client component, so a first
 * visitor who has never toggled the language falls back to the default locale
 * (zh-TW, the primary market) rather than to English.
 */
function readLocale(): WebLocale {
  if (typeof document === 'undefined') return DEFAULT_WEB_LOCALE
  const match = document.cookie.match(new RegExp(`(?:^|; )${UI_LOCALE_COOKIE}=([^;]*)`))
  const value = match ? decodeURIComponent(match[1]) : null
  return value === 'en' || value === 'zh-TW' ? value : DEFAULT_WEB_LOCALE
}

export default function GlobalError({
  error,
  retry,
}: {
  error: Error & { digest?: string }
  retry: () => void
}) {
  const locale = readLocale()
  const copy = COPY[locale]

  useEffect(() => {
    reportWebException(error, { boundary: 'global', digest: error.digest })
  }, [error])

  return (
    <html lang={locale}>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '24px',
          background: '#fff',
          color: '#161616',
          font: '16px/1.6 system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <main style={{ maxWidth: '42ch', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 600 }}>{copy.title}</h1>
          <p style={{ margin: '0 0 20px', color: '#5c5c5c' }}>{copy.description}</p>
          <button
            type="button"
            onClick={() => retry()}
            style={{
              minHeight: '44px',
              padding: '0 22px',
              border: 0,
              borderRadius: '8px',
              background: '#161616',
              color: '#fff',
              font: 'inherit',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copy.retry}
          </button>
          {/* The one thing support can use to find this exact failure in the
              server log. Rendered only when Next produced one. */}
          {error.digest ? (
            <p style={{ margin: '18px 0 0', fontSize: '12px', color: '#8a8a8a', fontFamily: 'ui-monospace, monospace' }}>
              {error.digest}
            </p>
          ) : null}
        </main>
      </body>
    </html>
  )
}
